import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Plus, RefreshCw, Trash2, Search, Info, AlertTriangle } from 'lucide-react';
import { Toaster } from './ui/sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Calendar } from './ui/calendar';
import { Progress } from './ui/progress';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { MarkdownLite } from './ui/markdown-lite';

/**
 * Hidden design-system reference page, reachable only at /design-system (no nav link).
 * Renders every ui/ primitive in active use with its computed CSS spec and usage counts,
 * as the source of truth for the ShadCN/Figma design-system migration. Counts and notes
 * were audited on 2026-08-10; refresh them if the component inventory shifts.
 */

const USAGE_MAX = 34;

const TOKEN_SWATCHES: { name: string; hex: string; cssVar: string }[] = [
  { name: 'primary', hex: '#795900', cssVar: '--primary' },
  { name: 'primary-container', hex: '#E9B32E', cssVar: '--primary-container' },
  { name: 'on-primary-container', hex: '#614700', cssVar: '--on-primary-container' },
  { name: 'headline (amber-900)', hex: '#78350F', cssVar: '--headline' },
  { name: 'tertiary', hex: '#8D4F11', cssVar: '--tertiary' },
  { name: 'tertiary-container', hex: '#FAA965', cssVar: '--tertiary-container' },
  { name: 'secondary-container', hex: '#EDDDD1', cssVar: '--secondary-container' },
  { name: 'background', hex: '#F9F8F3', cssVar: '--background' },
  { name: 'card / popover', hex: '#FFFFFF', cssVar: '--card' },
  { name: 'surface-container-low', hex: '#F5F3EF', cssVar: '--surface-container-low' },
  { name: 'muted', hex: '#EDEAE3', cssVar: '--muted' },
  { name: 'accent (hover)', hex: '#E8E4DC', cssVar: '--accent' },
  { name: 'foreground', hex: '#2A2826', cssVar: '--foreground' },
  { name: 'muted-foreground', hex: '#64605A', cssVar: '--muted-foreground' },
  { name: 'border', hex: '#E0DCD4', cssVar: '--border' },
  { name: 'input (border)', hex: '#C8C3BA', cssVar: '--input' },
  { name: 'outline-variant', hex: '#D3C4B2', cssVar: '--outline-variant' },
  { name: 'destructive', hex: '#4D1717', cssVar: '--destructive' },
  { name: 'success', hex: '#16A34A', cssVar: '--success' },
  { name: 'warning', hex: '#D97706', cssVar: '--warning' },
];

interface SpecSectionProps {
  id: string;
  title: string;
  file: string;
  count: number;
  countLabel?: string;
  spec: [string, string][];
  usedIn: string;
  children: ReactNode;
}

function SpecSection({ id, title, file, count, countLabel = 'files', spec, usedIn, children }: SpecSectionProps) {
  return (
    <section id={id} className="scroll-mt-20 rounded-xl border border-border bg-card shadow-sm">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 pt-4">
        <h3 className="font-display text-base font-bold">{title}</h3>
        <span className="font-mono text-xs text-muted-foreground">{file}</span>
        <span className="ml-auto flex min-w-[160px] items-center gap-2">
          <span className="text-sm font-semibold tabular-nums">{count}</span>
          <span className="text-xs text-muted-foreground">{countLabel}</span>
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <span
              className="block h-full rounded-full bg-[rgb(var(--primary-container))]"
              style={{ width: `${Math.max(4, Math.round((count / USAGE_MAX) * 100))}%` }}
            />
          </span>
        </span>
      </div>
      <div className="mt-3 grid md:grid-cols-[1.2fr_1fr]">
        <div className="flex flex-wrap items-center gap-3 border-t border-border bg-background p-5">
          {children}
        </div>
        <div className="border-t border-border p-5 md:border-l">
          <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            CSS spec
          </h4>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
            {spec.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="whitespace-nowrap text-muted-foreground">{k}</dt>
                <dd className="font-mono tabular-nums">{v}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground">Used in:</span> {usedIn}
          </p>
        </div>
      </div>
    </section>
  );
}

function NoteCallout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border border-l-[3px] border-l-[rgb(var(--primary-container))] bg-card px-4 py-3 text-sm">
      <span className="font-semibold">{title}</span> {children}
    </div>
  );
}

const TOC: [string, string][] = [
  ['tokens', 'Tokens'],
  ['button', 'Button'],
  ['label', 'Label'],
  ['input', 'Input'],
  ['card', 'Card'],
  ['badge', 'Badge'],
  ['dialog', 'Dialog'],
  ['textarea', 'Textarea'],
  ['select', 'Select'],
  ['alert-dialog', 'AlertDialog'],
  ['separator', 'Separator'],
  ['switch', 'Switch'],
  ['table', 'Table'],
  ['alert', 'Alert'],
  ['tooltip', 'Tooltip'],
  ['popover', 'Popover'],
  ['avatar', 'Avatar'],
  ['toast', 'Toast'],
  ['tabs', 'Tabs'],
  ['checkbox', 'Checkbox'],
  ['command', 'Command'],
  ['dropdown', 'DropdownMenu'],
  ['sheet', 'Sheet'],
  ['calendar', 'Calendar'],
  ['progress', 'Progress'],
  ['radio', 'RadioGroup'],
  ['markdown', 'MarkdownLite'],
  ['notes', 'Migration notes'],
];

export function DesignSystemPage() {
  const [calendarDate, setCalendarDate] = useState<Date | undefined>(new Date());
  const [switchOn, setSwitchOn] = useState(true);
  const [checked, setChecked] = useState(true);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster position="bottom-right" />
      <div className="mx-auto max-w-5xl px-6 pb-24 pt-10">
        <header className="border-b border-border pb-7">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
            upstand.ai internal reference
          </p>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Design system</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Every <span className="font-mono">components/ui</span> primitive in active use, rendered
            live with its computed CSS spec. All px values assume the app root font size of 14 px, so{' '}
            <span className="font-mono">text-sm</span> is 12.25 px and{' '}
            <span className="font-mono">h-8</span> is 28 px. This page is unlisted: it only exists at{' '}
            <span className="font-mono">/design-system</span>.
          </p>
          <nav className="mt-5 flex flex-wrap gap-1.5" aria-label="Sections">
            {TOC.map(([anchor, label]) => (
              <a
                key={anchor}
                href={`#${anchor}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:border-primary hover:text-primary"
              >
                {label}
              </a>
            ))}
          </nav>
        </header>

        {/* ---------------- Tokens ---------------- */}
        <section id="tokens" className="scroll-mt-20 pt-10">
          <h2 className="font-display text-lg font-bold">Design tokens</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Material Design 3 vocabulary (Stitch) on shadcn token names. Swatches read the live CSS
            variables; hex values are the light theme.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
            {TOKEN_SWATCHES.map((s) => (
              <div key={s.cssVar} className="overflow-hidden rounded-lg border border-border bg-card">
                <div
                  className="h-11 border-b border-border"
                  style={{ background: `rgb(var(${s.cssVar}))` }}
                />
                <div className="px-2.5 py-1.5">
                  <div className="text-[11px] font-semibold leading-tight">{s.name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{s.hex}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Type (root 14 px)</CardTitle>
              </CardHeader>
              <CardContent className="text-xs leading-6 text-muted-foreground">
                Display Manrope, body Inter. text-xs 10.5 / text-sm 12.25 / text-base 14 / text-lg
                15.75 / text-xl 17.5 px. Weights 400, 500, 600.
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Radius (computed)</CardTitle>
              </CardHeader>
              <CardContent className="text-xs leading-6 text-muted-foreground">
                rounded-sm 5.25 / rounded-md and -lg 10.5 / rounded-xl 14 / rounded-2xl 21 px.
                Checkbox is hardcoded 4 px; badges, pills and switches are fully round.
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Shadows and overlays</CardTitle>
              </CardHeader>
              <CardContent className="text-xs leading-6 text-muted-foreground">
                Untitled UI scale on rgba(16,24,40): shadow-sm on buttons and cards, shadow-md on
                popovers, shadow-lg on dialogs. Dialog overlay black 50%, Sheet overlay black 30%.
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ---------------- Components ---------------- */}
        <div className="mt-10 space-y-5">
          <SpecSection
            id="button"
            title="Button"
            file="ui/button.tsx"
            count={34}
            spec={[
              ['Height', '28 / 24.5 / 31.5 px, icon 28'],
              ['Padding', '10.5 x 5.25 px (default)'],
              ['Radius', '10.5 px'],
              ['Type', 'Inter 12.25 px / 500'],
              ['Primary', '#795900, hover 90%'],
              ['Secondary', '#E8E6E5 / #1D1D1D'],
              ['Outline', '1px #C8C3BA on #F9F8F3'],
              ['Destructive', '#4D1717 / #fff'],
              ['Focus', '2px ring #795900, offset 2px'],
            ]}
            usedIn="every surface (34 files). The sidebar New Meeting gradient (#795900 to #E9B32E) is a page-level override, not a variant."
          >
            <Button>Save changes</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
            <Button variant="destructive">Delete</Button>
            <Button size="sm">sm 24.5px</Button>
            <Button size="lg">lg 31.5px</Button>
            <Button size="icon" aria-label="Add">
              <Plus />
            </Button>
          </SpecSection>

          <SpecSection
            id="label"
            title="Label"
            file="ui/label.tsx"
            count={23}
            spec={[
              ['Type', 'Inter 12.25 px / 500, lh 1'],
              ['Layout', 'flex, gap 7 px for icons'],
              ['Disabled', 'opacity 50%'],
            ]}
            usedIn="every form: auth, wizards, settings suite, meetings, flyouts, attendee pages (23 files)."
          >
            <div className="grid w-64 gap-1.5">
              <Label htmlFor="ds-label-demo">Meeting name</Label>
              <Input id="ds-label-demo" placeholder="Weekly standup" />
            </div>
          </SpecSection>

          <SpecSection
            id="input"
            title="Input"
            file="ui/input.tsx"
            count={21}
            spec={[
              ['Height', '28 px'],
              ['Padding', '8.75 x 5.25 px'],
              ['Radius', '10.5 px'],
              ['Border', '1px #C8C3BA'],
              ['Fill', '#F9F8F3 (page cream, not white)'],
              ['Placeholder', '#64605A'],
              ['Focus', '2px ring #795900, offset 2px'],
            ]}
            usedIn="auth flows, all settings pages, meetings, wizards, share dialog, attendee gate (21 files). Note the fill is bg-background while Textarea fills white; pick one in Figma."
          >
            <div className="grid w-64 gap-2">
              <Input placeholder="you@company.com" />
              <Input value="Disabled" disabled readOnly />
            </div>
          </SpecSection>

          <SpecSection
            id="card"
            title="Card"
            file="ui/card.tsx"
            count={18}
            spec={[
              ['Fill', '#FFFFFF'],
              ['Border', '1px #D4D2D1 (gray-200, not --border)'],
              ['Radius', '10.5 px, shadow-sm'],
              ['Header', 'padding 14, row-gap 3.5 px'],
              ['Title / body', '14 px 500 / 12.25 px'],
              ['Content', 'padding 14, top 0'],
            ]}
            usedIn="all 6 settings pages, integrations, admin, leaderboard, the public pages and the role matrix (18 files)."
          >
            <Card className="w-72">
              <CardHeader>
                <CardTitle>Slack integration</CardTitle>
                <CardDescription>Send recaps and nudges to your channels.</CardDescription>
              </CardHeader>
              <CardContent>
                <Badge variant="success">Connected</Badge>
              </CardContent>
            </Card>
          </SpecSection>

          <SpecSection
            id="badge"
            title="Badge"
            file="ui/badge.tsx"
            count={12}
            spec={[
              ['Shape', 'pill, padding 8.75 x 1.75 px'],
              ['Type', 'Inter 10.5 px / 500'],
              ['default', '#795900 / #fff'],
              ['secondary', '#E8E6E5 / #1D1D1D'],
              ['success / warning', '#16A34A / #D97706'],
              ['destructive', '#4D1717 / #fff'],
              ['outline', '1px #C8C3BA, ink text'],
            ]}
            usedIn="meetings, integrations, auth, admin, user management, settings (12 files). Two variants beyond stock shadcn: success and warning."
          >
            <Badge>default</Badge>
            <Badge variant="secondary">secondary</Badge>
            <Badge variant="outline">outline</Badge>
            <Badge variant="success">success</Badge>
            <Badge variant="warning">warning</Badge>
            <Badge variant="destructive">destructive</Badge>
          </SpecSection>

          <SpecSection
            id="dialog"
            title="Dialog"
            file="ui/dialog.tsx"
            count={11}
            spec={[
              ['Fill', '#F9F8F3 (bg-background, not white)'],
              ['Width', 'max 448 px'],
              ['Padding', '21 px, row-gap 14 px'],
              ['Radius', '10.5 px, shadow-lg'],
              ['Title', '15.75 px / 600'],
              ['Overlay', 'black 50%'],
            ]}
            usedIn="meetings, integrations, action items, agent chat, workspace search, dashboard, share dialog, settings (11 files)."
          >
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Open dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Rename meeting series</DialogTitle>
                  <DialogDescription>
                    This updates the name everywhere, including past recaps.
                  </DialogDescription>
                </DialogHeader>
                <Input defaultValue="Weekly standup" />
                <DialogFooter>
                  <Button variant="outline">Cancel</Button>
                  <Button>Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </SpecSection>

          <SpecSection
            id="textarea"
            title="Textarea"
            file="ui/textarea.tsx"
            count={11}
            spec={[
              ['Min height', '56 px, auto-grows'],
              ['Padding', '10.5 x 7 px'],
              ['Radius', '10.5 px'],
              ['Border', '1px #C8C3BA'],
              ['Fill', '#FFFFFF (input-background)'],
              ['Focus', '3px soft ring #795900 at 50%'],
            ]}
            usedIn="check-in recorder, dashboard, meetings, agent chat, wizards, series flyout, share dialog, integrations, general settings (11 files). Uses the newer shadcn focus style; Input uses the older 2px hard ring."
          >
            <Textarea className="w-72" placeholder="What did you work on yesterday?" />
          </SpecSection>

          <SpecSection
            id="select"
            title="Select"
            file="ui/select.tsx"
            count={10}
            spec={[
              ['Trigger', 'same box as Input, 28 px'],
              ['Menu', '#fff, radius 10.5, shadow-md'],
              ['Item', '12.25 px, pad 7 to 28 px, radius 5.25'],
              ['Item focus', '#E8E4DC (accent)'],
            ]}
            usedIn="integrations, action items, meetings, admin, leaderboard, user management, settings (10 files)."
          >
            <Select defaultValue="standup">
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standup">Standup</SelectItem>
                <SelectItem value="one-on-one">1-1</SelectItem>
                <SelectItem value="team">Team meeting</SelectItem>
                <SelectItem value="retro">Retro</SelectItem>
              </SelectContent>
            </Select>
          </SpecSection>

          <SpecSection
            id="alert-dialog"
            title="AlertDialog"
            file="ui/alert-dialog.tsx"
            count={9}
            spec={[
              ['Box', 'identical to Dialog, no close X'],
              ['Action', 'Button default or destructive'],
              ['Cancel', 'Button outline'],
              ['Dismissal', 'buttons only'],
            ]}
            usedIn="meetings, admin, user management, share dialog, check-in recorder, settings (9 files). One Figma modal component with a confirm variant covers Dialog and AlertDialog."
          >
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  <Trash2 /> Remove member
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove this member?</AlertDialogTitle>
                  <AlertDialogDescription>
                    They lose access to all meetings in this workspace. Their past check-ins are
                    kept.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction>Remove member</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </SpecSection>

          <SpecSection
            id="separator"
            title="Separator"
            file="ui/separator.tsx"
            count={9}
            spec={[['Line', '1 px, #E0DCD4 (--border)']]}
            usedIn="auth, accept-invitation, attendee view, series flyout, unsubscribe, settings (9 files)."
          >
            <div className="w-56">
              <Separator />
            </div>
            <Separator orientation="vertical" className="h-7" />
          </SpecSection>

          <SpecSection
            id="switch"
            title="Switch"
            file="ui/switch.tsx"
            count={6}
            spec={[
              ['Track', '28 x 16 px, fully round'],
              ['Thumb', '14 px white circle'],
              ['On', '#795900, borderless'],
              ['Off', '#BBBDBC with 1px #9A9C9B border'],
            ]}
            usedIn="enrol wizard, series flyout, leaderboard, unsubscribe, settings general and personal (6 files)."
          >
            <div className="flex items-center gap-2">
              <Switch checked={switchOn} onCheckedChange={setSwitchOn} id="ds-switch" />
              <Label htmlFor="ds-switch">Record with AI bot</Label>
            </div>
          </SpecSection>

          <SpecSection
            id="table"
            title="Table"
            file="ui/table.tsx"
            count={6}
            spec={[
              ['Header row', '31.5 px, 10.5 px 500, #7A7C7B'],
              ['Cell padding', '10.5 px'],
              ['Row divider', '1px #E8E6E5 (gray-100)'],
              ['Hover / selected', '#F2F0EF (gray-50)'],
            ]}
            usedIn="user management, admin, leaderboard, billing, role matrix (6 files)."
          >
            <div className="w-full overflow-hidden rounded-lg border border-gray-200 bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Ana Duarte</TableCell>
                    <TableCell>Admin</TableCell>
                    <TableCell>
                      <Badge variant="success">Active</Badge>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Sam Okafor</TableCell>
                    <TableCell>Attendee</TableCell>
                    <TableCell>
                      <Badge variant="secondary">Invited</Badge>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </SpecSection>

          <SpecSection
            id="alert"
            title="Alert"
            file="ui/alert.tsx"
            count={5}
            spec={[
              ['Box', 'radius 10.5, 1px #E0DCD4, fill #fff'],
              ['Padding', '14 x 10.5 px'],
              ['Layout', '14 px icon + text, gap 10.5'],
              ['Destructive', 'all text #4D1717'],
            ]}
            usedIn="auth, accept-invitation, meetings, general settings (5 files)."
          >
            <div className="grid w-full max-w-sm gap-2.5">
              <Alert>
                <Info />
                <AlertTitle>Check your inbox</AlertTitle>
                <AlertDescription>We sent a magic link to your email address.</AlertDescription>
              </Alert>
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertTitle>Invitation expired</AlertTitle>
                <AlertDescription>Ask your admin to send a new one.</AlertDescription>
              </Alert>
            </div>
          </SpecSection>

          <SpecSection
            id="tooltip"
            title="Tooltip"
            file="ui/tooltip.tsx"
            count={5}
            spec={[
              ['Fill', '#795900 (primary, inverted)'],
              ['Text', '#fff, 10.5 px'],
              ['Padding', '10.5 x 5.25 px, radius 10.5'],
              ['Arrow', '8.75 px rotated square'],
            ]}
            usedIn="app top bar, dashboard, meetings, enrol wizard, leaderboard (5 files)."
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Regenerate">
                  <RefreshCw />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Regenerate insights</TooltipContent>
            </Tooltip>
            <span className="text-xs text-muted-foreground">hover the button</span>
          </SpecSection>

          <SpecSection
            id="popover"
            title="Popover"
            file="ui/popover.tsx"
            count={5}
            spec={[
              ['Width', '252 px default'],
              ['Fill', '#fff, border #E0DCD4'],
              ['Padding', '14 px, radius 10.5, shadow-md'],
            ]}
            usedIn="action items, agent chat, meeting occurrence picker (5 files)."
          >
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">Open popover</Button>
              </PopoverTrigger>
              <PopoverContent>
                <p className="text-sm font-medium">Filter by owner</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Pick a teammate to narrow the list.
                </p>
              </PopoverContent>
            </Popover>
          </SpecSection>

          <SpecSection
            id="avatar"
            title="Avatar"
            file="ui/avatar.tsx"
            count={4}
            spec={[
              ['Size', '35 px default (size-10)'],
              ['Fallback', '#EDEAE3, initials 12.25 px 500'],
            ]}
            usedIn="meetings, attendee view, user management, general settings (4 files). Pages often override size to 24 to 32 px."
          >
            <Avatar>
              <AvatarFallback>TH</AvatarFallback>
            </Avatar>
            <Avatar>
              <AvatarFallback className="bg-[rgb(var(--primary-container))] text-[rgb(var(--on-primary-container))]">
                AD
              </AvatarFallback>
            </Avatar>
            <Avatar className="size-6 text-[9px]">
              <AvatarFallback>SO</AvatarFallback>
            </Avatar>
          </SpecSection>

          <SpecSection
            id="toast"
            title="Toast (Sonner)"
            file="ui/sonner.tsx"
            count={26}
            countLabel="callers"
            spec={[
              ['Fill', '#fff (popover tokens)'],
              ['Border', '1px #E0DCD4, shadow-lg'],
              ['Title / body', '12.25 px 500 / #64605A'],
              ['Position', 'bottom-right'],
            ]}
            usedIn="mounted at the app root plus attendee pages; toast() fires from 26 files, the most-used feedback pattern in the app."
          >
            <Button
              variant="outline"
              onClick={() =>
                toast.success('Meeting enrolled', {
                  description: 'The bot will join 3 minutes before start.',
                })
              }
            >
              Fire success toast
            </Button>
            <Button variant="outline" onClick={() => toast.error('Could not reach the calendar')}>
              Fire error toast
            </Button>
          </SpecSection>

          <SpecSection
            id="tabs"
            title="Tabs"
            file="ui/tabs.tsx"
            count={3}
            spec={[
              ['List', '#EDEAE3 pill, h 31.5, pad 3, radius 14'],
              ['Trigger', '12.25 px / 500'],
              ['Active', '#fff fill, ink text'],
            ]}
            usedIn="auth (sign in / create account), admin, billing (3 files)."
          >
            <Tabs defaultValue="signin" className="w-72">
              <TabsList className="w-full">
                <TabsTrigger value="signin">Sign in</TabsTrigger>
                <TabsTrigger value="signup">Create account</TabsTrigger>
              </TabsList>
              <TabsContent value="signin" className="pt-2 text-xs text-muted-foreground">
                Sign-in form goes here.
              </TabsContent>
              <TabsContent value="signup" className="pt-2 text-xs text-muted-foreground">
                Sign-up form goes here.
              </TabsContent>
            </Tabs>
          </SpecSection>

          <SpecSection
            id="checkbox"
            title="Checkbox"
            file="ui/checkbox.tsx"
            count={3}
            spec={[
              ['Size', '14 x 14 px, radius 4 px'],
              ['Unchecked', '#fff fill, 1px #C8C3BA'],
              ['Checked', '#795900 fill, white check'],
            ]}
            usedIn="share dialog, voice settings (3 files)."
          >
            <div className="flex items-center gap-2">
              <Checkbox
                id="ds-check"
                checked={checked}
                onCheckedChange={(v) => setChecked(v === true)}
              />
              <Label htmlFor="ds-check">Include transcript link</Label>
            </div>
          </SpecSection>

          <SpecSection
            id="command"
            title="Command"
            file="ui/command.tsx"
            count={3}
            spec={[
              ['Shell', '#fff, radius 10.5, shadow-lg'],
              ['Search row', '1px bottom border'],
              ['Group heading', '10.5 px #64605A'],
              ['Item', '12.25 px, focus #E8E4DC'],
            ]}
            usedIn="workspace search (global cmd-K palette), meetings, action items (3 files)."
          >
            <Command className="w-full max-w-xs rounded-lg border border-border shadow-md">
              <CommandInput placeholder="Search meetings, people..." />
              <CommandList>
                <CommandEmpty>No results.</CommandEmpty>
                <CommandGroup heading="Meetings">
                  <CommandItem>
                    <Search /> Weekly standup
                  </CommandItem>
                  <CommandItem>
                    <Search /> Product sync
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </SpecSection>

          <SpecSection
            id="dropdown"
            title="DropdownMenu"
            file="ui/dropdown-menu.tsx"
            count={3}
            spec={[
              ['Menu', '#fff, radius 10.5, pad 3.5, shadow-lg'],
              ['Item', '12.25 px, radius 5.25, focus #E8E4DC'],
              ['Destructive item', '#4D1717'],
              ['Separator', '1px #E0DCD4'],
            ]}
            usedIn="meetings row actions, user management (3 files)."
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">Open menu</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>tim@futureofx.co</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>Switch workspace</DropdownMenuItem>
                <DropdownMenuItem>Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive">Remove member</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SpecSection>

          <SpecSection
            id="sheet"
            title="Sheet"
            file="ui/sheet.tsx"
            count={2}
            spec={[
              ['Fill', '#F9F8F3 (bg-background)'],
              ['Width', 'up to 672 px, right side'],
              ['Header / footer', 'pad 14, 1px border'],
              ['Overlay', 'black 30% (lighter than Dialog)'],
            ]}
            usedIn="series settings flyout, user management member detail (2 files)."
          >
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline">Open sheet</Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Series settings</SheetTitle>
                  <SheetDescription>Weekly standup, Mondays 9:30</SheetDescription>
                </SheetHeader>
                <div className="p-4 text-sm text-muted-foreground">
                  Bot, agenda and permission controls live here.
                </div>
                <SheetFooter>
                  <Button variant="outline">Close</Button>
                  <Button>Save</Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </SpecSection>

          <SpecSection
            id="calendar"
            title="Calendar"
            file="ui/calendar.tsx"
            count={1}
            countLabel="file"
            spec={[
              ['Base', 'react-day-picker, pad 10.5'],
              ['Day cell', '28 px ghost button, radius 10.5'],
              ['Selected', '#795900 / #fff'],
              ['Today', '#E8E4DC (accent)'],
            ]}
            usedIn="meeting occurrence picker only (inside a Popover), which serves meetings and the attendee view."
          >
            <Calendar
              mode="single"
              selected={calendarDate}
              onSelect={setCalendarDate}
              className="rounded-lg border border-border bg-card"
            />
          </SpecSection>

          <SpecSection
            id="progress"
            title="Progress"
            file="ui/progress.tsx"
            count={1}
            countLabel="file"
            spec={[
              ['Track', '7 px tall, #795900 at 20%'],
              ['Fill', '#795900, fully round'],
            ]}
            usedIn="billing plan usage only. The onboarding wizard draws its own progress bar from custom tokens."
          >
            <Progress value={62} className="w-56" />
          </SpecSection>

          <SpecSection
            id="radio"
            title="RadioGroup"
            file="ui/radio-group.tsx"
            count={1}
            countLabel="file"
            spec={[
              ['Size', '14 px circle, 1px #C8C3BA'],
              ['Checked', '#795900 ring and dot'],
            ]}
            usedIn="share series dialog only."
          >
            <RadioGroup defaultValue="link" className="flex gap-5">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="link" id="ds-r1" />
                <Label htmlFor="ds-r1">Anyone with link</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="workspace" id="ds-r2" />
                <Label htmlFor="ds-r2">Workspace only</Label>
              </div>
            </RadioGroup>
          </SpecSection>

          <SpecSection
            id="markdown"
            title="MarkdownLite"
            file="ui/markdown-lite.tsx (custom, not shadcn)"
            count={1}
            countLabel="file"
            spec={[
              ['Role', 'renders AI summaries and notes'],
              ['Supports', 'headers, bullets, bold, paragraphs'],
            ]}
            usedIn="meetings insights and recap rendering. Cover it with text styles in Figma rather than a component."
          >
            <MarkdownLite
              className="max-w-sm text-sm"
              text={
                '## Decisions\n- Ship the retention preset behind a flag\n- Tim to update the **org settings** card'
              }
            />
          </SpecSection>
        </div>

        {/* ---------------- Migration notes ---------------- */}
        <section id="notes" className="scroll-mt-20 pt-12">
          <h2 className="font-display text-lg font-bold">Migration notes</h2>
          <div className="mt-4 space-y-3">
            <NoteCallout title="Fill inconsistencies to resolve in Figma first:">
              Input fills page cream #F9F8F3 but Textarea fills white. Dialog and Sheet fill page
              cream while Popover, Dropdown, Command and Toast fill white. Card borders with
              gray-200 #D4D2D1 instead of the semantic border token #E0DCD4.
            </NoteCallout>
            <NoteCallout title="Two focus-ring generations coexist:">
              Button, Input and Select use the older 2 px hard ring with offset; Textarea, Switch
              and Checkbox use the newer 3 px soft ring at 50% opacity. Standardize on one.
            </NoteCallout>
            <NoteCallout title="The dark theme is a different brand.">
              The .dark block swaps primary to violet #8B5CF6 on slate-blue grounds (Untitled UI
              leftovers) and does not restyle the ochre identity. Decide whether dark mode is in
              scope before mirroring it in Figma.
            </NoteCallout>
            <NoteCallout title="Skip these in Figma (installed but never imported):">
              accordion, aspect-ratio, breadcrumb, carousel, chart, collapsible, context-menu,
              drawer, form, hover-card, input-otp, menubar, navigation-menu, resizable, slider,
              toggle, toggle-group and ui/sidebar, plus skeleton, scroll-area and pagination whose
              only consumers are dead files (audit-log, advanced-data-table-v2, filter-popover,
              invitation-cleanup-dialog, resend-diagnostic).
            </NoteCallout>
            <NoteCallout title="Custom compositions to build on top of the primitives:">
              Sidebar (pill nav plus gradient CTA), AppTopBar, WorkspaceFlowShell with its segmented
              stepper, CheckInRecorder, MeetingOccurrencePicker, AgentChat, WorkspaceSearch (cmd-K),
              SeriesSettingsFlyout, ShareSeriesDialog and RolePermissionsMatrix.
            </NoteCallout>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Audited 2026-08-10 from packages/frontend/src. Counts are importing files, not render
            sites.
          </p>
        </section>
      </div>
    </div>
  );
}
